export interface AttachedImage {
    id: string
    file: File
    preview: string
    size: number
    error?: string
}

const IMAGE_TYPES = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif']
const MAX_IMAGE_SIZE = 20 * 1024 * 1024
const MAX_IMAGES = 5
const MAX_IMAGE_PAYLOAD_BYTES = 20 * 1024 * 1024

export const MAX_ATTACHED_IMAGES = MAX_IMAGES

function readFileAsDataURL(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(reader.result as string)
        reader.onerror = () => reject(new Error('Failed to read image'))
        reader.readAsDataURL(file)
    })
}

export function validateImageFile(file: File): { valid: boolean; error?: string } {
    const mime = file.type.toLowerCase()
    const ext = file.name.split('.').pop()?.toLowerCase() || ''
    const isImage =
        IMAGE_TYPES.includes(mime) ||
        ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)

    if (!isImage) {
        return { valid: false, error: 'Only image files are supported (PNG, JPEG, WebP, GIF).' }
    }
    if (file.size > MAX_IMAGE_SIZE) {
        return { valid: false, error: `Image too large (max ${MAX_IMAGE_SIZE / (1024 * 1024)}MB).` }
    }
    return { valid: true }
}

export async function createAttachedImage(file: File): Promise<AttachedImage> {
    const validation = validateImageFile(file)
    if (!validation.valid) {
        return {
            id: crypto.randomUUID(),
            file,
            preview: '',
            size: file.size,
            error: validation.error,
        }
    }
    const preview = await readFileAsDataURL(file)
    return {
        id: crypto.randomUUID(),
        file,
        preview,
        size: file.size,
    }
}

export async function processImageFiles(
    files: File[],
    existing: AttachedImage[],
): Promise<AttachedImage[]> {
    const next = [...existing]
    for (const file of files) {
        if (next.filter((img) => !img.error).length >= MAX_IMAGES) break
        next.push(await createAttachedImage(file))
    }
    return next
}

export async function collectImageDataUrls(
    images: AttachedImage[],
): Promise<{ dataUrls: string[]; error?: string }> {
    const valid = images.filter((img) => !img.error && img.preview)
    const dataUrls: string[] = []
    let totalBytes = 0

    for (const img of valid) {
        const preview = img.preview.trim()
        if (!preview.startsWith('data:image/') || !preview.includes(';base64,')) continue
        const base64Part = preview.split(',')[1]
        if (!base64Part) continue
        totalBytes += base64Part.length * 0.75
        dataUrls.push(preview)
    }

    if (totalBytes > MAX_IMAGE_PAYLOAD_BYTES) {
        return {
            dataUrls: [],
            error: `Image payload too large (${(totalBytes / (1024 * 1024)).toFixed(1)}MB). Remove some images.`,
        }
    }

    return { dataUrls }
}
